import { useState } from "react";
import { X } from "lucide-react";
import { useDash, ago } from "../../../lib/dash";
import { Tile, TileState, Chip } from "../Tile";
import { ROLE_LABEL } from "./InviteDialog";
import { ROLE_TONE, STATUS_TONE } from "./AccountList";
import { AccountActions } from "./AccountActions";
import { SpendSummary } from "./SpendSummary";
import { AllowanceForm } from "./AllowanceForm";
import { PartnerOrders } from "./PartnerOrders";

const Head = ({ a }) => (
  <div data-testid="account-panel-head" className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
    <span data-testid="account-panel-email" className="mr-1 max-w-full truncate font-mono2 text-[12px] text-foreground">{a.email}</span>
    <Chip testid="account-panel-role" tone={ROLE_TONE[a.role]}>{ROLE_LABEL[a.role]}</Chip>
    <Chip testid="account-panel-status" tone={STATUS_TONE[a.status]}>{a.status}</Chip>
    {a.two_step && <Chip tone="green">2-step on</Chip>}
    <span data-testid="account-panel-meta">
      last sign-in {ago(a.last_login_at)}{a.role_source ? ` · role set by ${a.role_source}` : ""}
    </span>
  </div>
);

const SubTabs = ({ tabs, tab, onTab }) => (
  <div data-testid="account-subtabs" className="mt-3 flex gap-1 border-b border-border">
    {tabs.map(([key, label]) => (
      <button key={key} type="button" data-testid={`account-subtab-${key}`} aria-current={tab === key ? "page" : undefined} onClick={() => onTab(key)}
        className={`-mb-px border-b-2 px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
        {label}
      </button>
    ))}
  </div>
);

const tabsFor = (d) => [["summary", "Ducks & spend"],
  ...(d.can_manage ? [["allowance", "Give back allowance"]] : []),
  ...(d.account.role === "partner" ? [["orders", "Partner orders"]] : [])];

// The picked account: who it is, what can be changed (with a confirm step), its ducks / spend / credits.
export const AccountPanel = ({ id, onClose, onChanged }) => {
  const s = useDash(`/accounts/${id}`, { poll: false });
  const [tab, setTab] = useState("summary");
  const d = s.data;
  const changed = () => { s.reload(); onChanged(); };
  const tabs = d ? tabsFor(d) : [];
  const current = tabs.some(([k]) => k === tab) ? tab : "summary";
  return (
    <Tile id="account-panel" index={1} kicker="Account" title={d ? d.account.name || d.account.email : "Loading"}
      action={<button type="button" data-testid="account-panel-close" onClick={onClose} aria-label="Close the account"
        className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>}>
      <TileState state={s}>
        {d && (
          <>
            <Head a={d.account} />
            <AccountActions d={d} onChanged={changed} />
            <SubTabs tabs={tabs} tab={current} onTab={setTab} />
            <div className="dash-scroll mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
              {current === "summary" && <SpendSummary d={d} />}
              {current === "allowance" && <AllowanceForm id={id} spend={d.spend} onDone={changed} />}
              {current === "orders" && <PartnerOrders id={id} canManage={d.can_manage} />}
            </div>
          </>
        )}
      </TileState>
    </Tile>
  );
};
